import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { AdminDashboardResponse, AdminFacilityDetail } from "../data/types";

export interface AdminDashboardOutageEvent {
  facilityId: number;
  orgId: number;
  isOngoing: boolean;
  // Whether ANY facility in this org currently has an ongoing outage,
  // computed by the caller from the authoritative fleet-wide outage store
  // (useFleetOutageStore) rather than derived here — GET /admin/dashboard's
  // list response doesn't reliably carry an inline per-org facilities array
  // to recompute this from locally (org.facilities is null in practice for
  // real orgs; AdminDashboard.tsx only reads it as a virtual-org fallback).
  // An earlier version of this function tried to derive the org flag from
  // that array and silently no-op'd for every real org as a result — found
  // via live testing (JAA-1095): the badge never updated at all.
  orgHasAnyOngoingOutage: boolean;
}

/**
 * Direct-patches hasOngoingOutage into the two Admin Dashboard caches that
 * carry it — the paginated org list (adminDashboard) and the on-demand
 * expanded-org facility list (orgFacilities) — from a live event:outage push
 * (JAA-1095). Replaces the 5-minute refetchInterval both queries used to
 * carry solely for this field (see useAdminDashboard.ts); everything else in
 * their responses (user/facility counts, etc.) has no push channel and keeps
 * its default staleness instead of being kept artificially fresh on a timer.
 */
export function patchAdminDashboardOutageCache(
  queryClient: QueryClient,
  outage: AdminDashboardOutageEvent,
): void {
  queryClient.setQueriesData<InfiniteData<AdminDashboardResponse>>(
    { queryKey: ["adminDashboard"] },
    (data) => {
      if (!data) return data;

      let changed = false;
      const pages = data.pages.map((page) => {
        let pageChanged = false;
        const items = page.organizations.items.map((org) => {
          if (org.id !== outage.orgId) return org;
          pageChanged = true;

          const facilityIndex = org.facilities?.findIndex(
            (facility) => facility.id === outage.facilityId,
          );
          const facilities =
            org.facilities && facilityIndex !== undefined && facilityIndex !== -1
              ? org.facilities.map((facility, i) =>
                  i === facilityIndex
                    ? { ...facility, hasOngoingOutage: outage.isOngoing }
                    : facility,
                )
              : org.facilities;

          return {
            ...org,
            facilities,
            hasOngoingOutage: outage.orgHasAnyOngoingOutage,
          };
        });

        if (!pageChanged) return page;
        changed = true;
        return { ...page, organizations: { ...page.organizations, items } };
      });

      return changed ? { ...data, pages } : data;
    },
  );

  queryClient.setQueriesData<AdminFacilityDetail[]>(
    { queryKey: ["orgFacilities", outage.orgId] },
    (facilities) => {
      if (!facilities) return facilities;

      const facilityIndex = facilities.findIndex(
        (facility) => facility.id === outage.facilityId,
      );
      if (facilityIndex === -1) return facilities;

      return facilities.map((facility, i) =>
        i === facilityIndex
          ? { ...facility, hasOngoingOutage: outage.isOngoing }
          : facility,
      );
    },
  );
}
